import type { CSSProperties } from 'react';
import type { GridLayout } from '@store/dashboardStore';

/**
 * Literal `N × N` grid sizing for the dashboard.
 *
 * Each layout maps to:
 *   - `gridTemplateColumns`: exactly `N` equal columns.
 *   - `gridTemplateRows`:    exactly `N` rows, each `minmax(minRow, 1fr)`.
 *   - `gridAutoRows`:        same `minmax(minRow, 1fr)` so cameras beyond
 *                            `N²` keep an identical cell height as the grid
 *                            scrolls.
 *
 * The `minmax(minRow, 1fr)` pattern is the crucial bit: rows expand to share
 * the container height when there's room (so a 2×2 truly fills the viewport
 * on tall screens), but never collapse below a minimum that keeps each
 * camera readable on shorter screens — the grid scrolls instead.
 */
const COL_COUNT: Record<GridLayout, number> = {
  '1x1': 1,
  '2x2': 2,
  '3x3': 3,
  '4x4': 4,
};

/**
 * Minimum row height per layout, in pixels. Tuned so the live preview keeps a
 * usable size at each density on a typical 1080p workstation viewport.
 */
const MIN_ROW_HEIGHT_PX: Record<GridLayout, number> = {
  '1x1': 520,
  '2x2': 400,
  '3x3': 300,
  '4x4': 240,
};

export function gridStyleFor(layout: GridLayout): CSSProperties {
  const cols = COL_COUNT[layout];
  const minRow = `${MIN_ROW_HEIGHT_PX[layout]}px`;
  const rowTrack = `minmax(${minRow}, 1fr)`;
  return {
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${cols}, ${rowTrack})`,
    gridAutoRows: rowTrack,
  };
}
